// src/pages/auth/AzureCallbackHandler.tsx
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../../api/auth/authService";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { fetchCurrentUser } from "../../store/slices/misc/currentUser";
import { Spinner, Alert } from "reactstrap";

const AzureCallbackHandler = ({
  setIsLoggedIn,
}: {
  setIsLoggedIn: (v: boolean) => void;
}) => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserMapper = {
      Edg: "Microsoft Edge",
      OPR: "Opera",
      Chrome: "Chrome",
      Safari: "Safari",
      Firefox: "Firefox",
      MSIE: "Internet Explorer",
    };

    let browserName = "Unknown";
    for (const key in browserMapper) {
      if (userAgent.indexOf(key) > -1) {
        browserName = browserMapper[key as keyof typeof browserMapper];
        break;
      }
    }
    return browserName;
  };

  const getIpAddress = async () => {
    try {
      const response: any = await axios.get(
        "https://api.ipify.org?format=json"
      );
      return response.data.ip;
    } catch (error) {
      console.error("Error fetching IP address: ", error);
      return "";
    }
  };

  const exchangeCodeForToken = async (code: string) => {
    console.log("code", code);
    setLoading(true);
    try {
      const response = await authService.codeExchangeForAccessToken({
        code: code,
        redirectUri: process.env.REACT_APP_AZURE_REDIRECT_URI,
        applicationType: "Buyer",
      });

      const { status, token, message } = response;

      if (status && token?.access_token) {
        Cookies.set("access-token", JSON.stringify(token), {
          expires: 7,
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set("refresh-token", token.refresh_token, {
          expires: 7,
          secure: true,
          sameSite: "Strict",
        });

        // Store login method for app switching
        localStorage.setItem("loginMethod", "azure");

        // Fetch user details
        dispatch(fetchCurrentUser());

        // Log login activity
        const ipAddress = await getIpAddress();
        authService.createLoginActivity({
          deviceInfo: getBrowserInfo(),
          ipAddress,
          loginDateTime: new Date().toISOString(),
        });

        setLoading(false);
        setIsLoggedIn(true);
        navigate("/home");
      } else {
        // API returned but status is false
        setError(message || "Azure login failed. Please try again.");
        setLoading(false);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/auth-login");
        }, 3000);
      }
    } catch (error: any) {
      console.error("Token exchange failed:", error);
      setError(
        error?.response?.data?.message ||
        error?.message ||
        "Azure authentication failed. Please try again."
      );
      setLoading(false);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/auth-login");
      }, 3000);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get("code");
    const errorParam = queryParams.get("error");
    const errorDescription = queryParams.get("error_description");

    if (errorParam) {
      // Azure returned an error (user cancelled, etc.)
      setError(errorDescription || "Azure login was cancelled or failed.");
      setLoading(false);
      setTimeout(() => {
        navigate("/auth-login");
      }, 3000);
    } else if (code) {
      exchangeCodeForToken(code);
    } else {
      setError("No authorization code received from Azure.");
      setLoading(false);
      setTimeout(() => {
        navigate("/auth-login");
      }, 3000);
    }
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        padding: "20px",
      }}
    >
      {loading && (
        <>
          <Spinner color="primary" />
          <p style={{ marginTop: "20px" }}>Signing you in with Azure...</p>
        </>
      )}

      {error && (
        <div style={{ maxWidth: "500px", width: "100%", textAlign: "center" }}>
          <Alert color="danger">
            <h5>Authentication Failed</h5>
            <p>{error}</p>
            <p style={{ marginTop: "10px", fontSize: "14px" }}>
              Redirecting to login page in 3 seconds...
            </p>
          </Alert>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/auth-login")}
          >
            Go to Login Now
          </button>
        </div>
      )}
    </div>
  );
};

export default AzureCallbackHandler;
